import {
  parseNullablePrice,
} from './utils/priceUtils.js'

import {
  pickMainVariation
} from './utils/productUtils.js'

import { OPTION_COLOR_SEPARATOR } from '@utils/Store/productUtils.js'


/* ---------------------------------------------------- */

const TYPE = {
  LEGACY: 'LEGACY',
  SHOPIFY: 'SHOPIFY',
  DEFAULT: 'DEFAULT',
}

/* ---------------------------------------------------- */

class ProductImage {
  constructor(data) {
    this.id = data.id
    this.src = data.originalSrc || data.src
    this.title = data.title || data.altText || null
    this.tag = data.tag || {}
    this.type = (data.type || '').toUpperCase() // VIDEO only, default is image
    this.videoUrl = data.videoUrl
  }
}

/* ---------------------------------------------------- */

class ProductVariant {
  constructor(data) {

    this.id = data.id
    this.title = data.title
    this.sku = data.sku

    this.msrp = parseNullablePrice(data.msrp)
    this.price = data.price
    this.currencySymbol = data.currencySymbol
    this.currencyCode = data.currencyCode
    this.displayPrice = data.displayPrice
    this.displayMsrp = data.displayMsrp
    this.saveFee = data.saveFee
    this.displaySaveFee = data.displaySaveFee
    this.installmentPrice = data.installmentPrice
    this.displayInstallmentPrice = data.displayInstallmentPrice

    this.image = data.image ? new ProductImage(data.image) : ({ src: data.src })

    this.selectedOptions = data.selectedOptions

    this.availableForSale = data.availableForSale

    this.priceTag = data.priceTag
  }
}

class DynamicProductVariant {
  constructor(data) {

    this.id = data.id

    this.msrp = parseNullablePrice(data.msrp)
    this.price = data.price

    this.displayPrice = data.displayPrice
    this.displayMsrp = data.displayMsrp

    this.availableForSale = data.availableForSale
  }
}

/* ---------------------------------------------------- */

class ProductSizeInfo {

  constructor(data) {
    this.sizeChartDisplayList = data.sizeChartDisplayList
    this.sizeChartTemplate = data.sizeChartTemplate
    this.sizeGuideMeta = data.sizeGuideMeta
  }
}

class Product {
  constructor(data) {
    this.id = data.id

    this.handle = data.handle

    this.variations	= (data.variations || []).map(item => new ProductVariant(item))

    this.mainVariation = data.mainVariation
      ? new ProductVariant(data.mainVariation)
      : pickMainVariation({
        variations: this.variations
      })

    this.images = (data.images || []).map(item => new ProductImage(item))
    this.mainImage = this.images[0]

    this.options = data.options || []
    this.colorSwatchList = data.colorSwatchList || []

    this.title = data.title

    this.category = data.category
    this.tags = data.tags || []
    this.isNew = data.tags && data.tags.indexOf('New Arrival') > -1

    this.availableForSale = Boolean(data.availableForSale)

    this.meta = data.meta1 || {}
    this.models = data.models || []
    this.flamingReels = data.flamingReels || []

    this.collection = data.collection

    this.bodyMeasurementDisplayFlag = data.bodyMeasurementDisplayFlag !== false

  }
}

class ProductFeed {
  constructor(data) {
    this.id = data.id

    this.handle = data.handle

    this.mainVariation = data.mainVariation
      ? new ProductVariant(data.mainVariation)
      : pickMainVariation({
        variations: this.variations
      })

    this.mainImage = data.mainImage
      ? new ProductImage(data.mainImage)
      : this.images[0] || null


    this.title = data.title
    this.category = data.category
    this.colorSwatchList = data.colorSwatchList || []
    this.tags = data.tags || []
    this.promotions = data.promotions
    this.isFlashSale = data.promotions && data.promotions.filter(i => i === 'FlashSale').length > 0
    this.soldOutPercent = data.soldOutPercent
    this.promotionBar = data.promotionBar
    this.promotionTag = data.promotionTag
  }
}

class ColorSwatchItem {
  constructor(data) {
    this.color = data.color
    this.optionValue = data.optionValue
    this.productImageUrl = data.originProductImageUrl || data.productImageUrl
    this.colorImageLink = data.originColorImageLink || data.colorImageLink
    this.colorImageUrl = data.originColorImageUrl || data.colorImageUrl
  }
}

class ESProductFeed {
  constructor(data) {
    this.id = data.id

    this.handle = data.handle

    this.mainVariation = {
      msrp: parseNullablePrice(data.originPrice),
      price: data.price,
      priceTag: data.priceTag,
      currencySymbol: data.symbol,
      displayPrice: data.displayPrice,
      displayMsrp: data.displayOriginPrice,
      saveFee: data.saveFee,
      displaySaveFee: data.displaySaveFee,
    }

    this.mainImage = {
      src: data.originImg || data.img
    }

    this.title = data.title
    this.category = data.category
    this.tags = data.tags || []
    this.isNew = data.tags && data.tags.indexOf('New Arrival') > -1
    this.promotions = data.promotions
    this.isFlashSale = data.promotions && data.promotions.filter(i => i === 'FlashSale').length > 0
    this.soldOutPercent = data.soldOutPercent
    this.colorSwatchList = (data.colorSwatchList || []).map(item => new ColorSwatchItem(item))
    this.promotionBar = data.promotionBar
    this.promotionTag = data.promotionTag
  }
}


const processOptions = (listingOptions) => {
  const colorSwatchList = []
  const options = []

  listingOptions.forEach(option => {
    if (option.n === 'Color') {
      const values = []
      option.v.forEach(value => {
        const colorValue = `${value.ov}${OPTION_COLOR_SEPARATOR}${value.h}`
        values.push(colorValue)
        colorSwatchList.push({
          color: value.cs.c,
          colorImageLink: value.cs.i ? `https://img.flamingo.shop${value.cs.i}` : '',
          optionValue: colorValue
        })
      })
      options.push({
        name: option.n,
        displayName: option.dn,
        values
      })
    } else {
      options.push({
        name: option.n,
        displayName: option.dn,
        values: option.v
      })
    }
  })

  return { options, colorSwatchList }
}

const processVariations = (listingSku) => {
  return listingSku.map(sku => ({
    id: sku.id,
    handle: sku.h,
    msrp: parseNullablePrice(sku.mr),
    price: sku.pr,
    displayPrice: sku.dp,
    displayMsrp: sku.dm,
    installmentPrice: sku.in,
    displayInstallmentPrice: sku.di,
    sku: sku.sk,
    title: sku.t,
    availableForSale: sku.a,
    selectedOptions: sku.op.map(selectedOption => {
      if (selectedOption.n === 'Color') {
        return {
          name: selectedOption.n,
          value: `${selectedOption.vl}${OPTION_COLOR_SEPARATOR}${sku.h}`
        }
      } else {
        return {
          name: selectedOption.n,
          value: selectedOption.vl
        }
      }
    })
  }))
}

const processListingProductMap = productMap => {
  const result = {}

  Object.keys(productMap).forEach(handle => {
    const product = productMap[handle]

    result[handle] = {
      id: product.id,
      title: product.t,
      handle,
      images: product.im.map(item => ({
        id: item.id,
        src: `https://img.flamingo.shop${item.sr}`,
        tag: {
          optionValue: item.tg && item.tg.op
        },
        type: (item.ty || '').toUpperCase(),
        videoUrl: item.vi ? `https://media.flamingo.shop${item.vi}` : ''
      })),
      availableForSale: Boolean(product.a),
      sizeChartDisplayList: product.sc.map(sItem => ({
        size: sItem.s,
        displayValue: sItem.d
      })),
      comment: {
        comments: [],
        count: product.cm && product.cm.cn,
        star: product.cm && product.cm.st
      }
    }
  })

  return result
}

class ProductListing {
  constructor(data) {
    this.isListing = data.le

    // 处理 options 和 colorSwatchList
    const { options, colorSwatchList } = processOptions(data.lo || [])
    this.options = options
    this.colorSwatchList = colorSwatchList

    this.listingVariations = processVariations(data.ls || [])
  }
}

class ListingProductMap {
  constructor(data) {
    this.productMap = processListingProductMap(data.p || {})
  }
}

export { TYPE, ProductVariant, DynamicProductVariant, ProductFeed, ESProductFeed, ProductSizeInfo, ProductListing, ListingProductMap }

export default Product
